import { Card, ErrorBox, Link, Loading, Page, Stat, Table, Td } from '@elsa/ui';
import { useQuery } from '@tanstack/react-query';
import { fmtDay, fmtLei, fmtNum } from '../lib/format.ts';
import { rpc } from '../lib/supabase.ts';
import type { EntityProfile } from '../lib/types.ts';
import { Results } from './Search.tsx';

// Fisa unei autoritati contractante sau a unui furnizor: datele, totalurile, CPV-urile si
// partenerii principali, apoi achizitiile (aceeasi lista ca la cautare, filtrata pe entitate).
export function EntityPage({ kind, id }: { kind: 'authority' | 'supplier'; id: number }) {
  const q = useQuery({
    queryKey: ['entity', kind, id],
    queryFn: () => rpc<EntityProfile | null>('entity', { p_kind: kind, p_id: id }),
  });
  const label = kind === 'authority' ? 'Autoritatea contractantă' : 'Furnizorul';
  if (q.isLoading) return <Page title={label}>{<Loading />}</Page>;
  if (q.error) return <Page title={label}>{<ErrorBox error={q.error} />}</Page>;
  const p = q.data;
  if (!p) {
    return (
      <Page title={label}>
        <p className="text-sm text-gri-800">Nu există sau firma nu are un abonament activ.</p>
      </Page>
    );
  }
  const e = p.entity;
  const partner = kind === 'authority' ? '/app/furnizori/' : '/app/autoritati/';
  return (
    <Page title={e.entity_name ?? `${label} ${id}`}>
      <p className="-mt-2 text-sm text-gri-800">
        {label}
        {e.numeric_fiscal_number ? ` · CUI ${e.numeric_fiscal_number}` : ''}
        {[e.city, e.county].filter(Boolean).length
          ? ` · ${[e.city, e.county].filter(Boolean).join(', ')}`
          : ''}
        {e.email ? ` · ${e.email}` : ''}
        {e.phone ? ` · ${e.phone}` : ''}
      </p>
      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="Achiziții (în istoricul planului)" value={fmtNum(p.stats.acquisitions)} />
        <Stat label="Valoare totală" value={fmtLei(p.stats.total_value_ron)} />
        <Stat
          label="Perioada"
          value={`${fmtDay(p.stats.first_day)} – ${fmtDay(p.stats.last_day)}`}
        />
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        <Card title="Coduri CPV principale">
          <Table head={['CPV', 'Achiziții', 'Valoare']} empty={!p.top_cpv.length}>
            {p.top_cpv.map((c) => (
              <tr key={c.cpv_code}>
                <Td className="max-w-xs">
                  <Link to={`/app/preturi/${c.cpv_code}`} className="hover:underline">
                    {c.cpv_code}
                  </Link>
                  <div className="text-xs text-gri-700">{c.cpv_label}</div>
                </Td>
                <Td className="tabular-nums">{fmtNum(c.acquisitions)}</Td>
                <Td className="text-right tabular-nums">{fmtLei(c.total_value_ron)}</Td>
              </tr>
            ))}
          </Table>
        </Card>
        <Card title={kind === 'authority' ? 'Furnizori principali' : 'Clienți principali'}>
          <Table head={['Nume', 'Achiziții', 'Valoare']} empty={!p.top_partners.length}>
            {p.top_partners.map((x) => (
              <tr key={x.id}>
                <Td className="max-w-xs">
                  <Link to={`${partner}${x.id}`} className="hover:underline">
                    {x.name ?? x.id}
                  </Link>
                </Td>
                <Td className="tabular-nums">{fmtNum(x.acquisitions)}</Td>
                <Td className="text-right tabular-nums">{fmtLei(x.total_value_ron)}</Td>
              </tr>
            ))}
          </Table>
        </Card>
      </div>
      <Results
        kind="acq"
        params={kind === 'authority' ? { authority_id: id } : { supplier_id: id }}
      />
    </Page>
  );
}
